import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Table, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import './ViewFoundation.css'; // Reuse foundation styles
import BackToDashboard from '../components/BackToDashboard';

function ViewBusinessModel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Business Model Data
  const [coreProduct, setCoreProduct] = useState('');
  const [pricingData, setPricingData] = useState(null);
  const [recurringModel, setRecurringModel] = useState(null);
  const [targetAudience, setTargetAudience] = useState('');
  const [competitorCount, setCompetitorCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [upgradePaths, setUpgradePaths] = useState([]);
  const [usp, setUSP] = useState('');
  const [offer, setOffer] = useState(null);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [originalFeatures, setOriginalFeatures] = useState([]);
  const [originalBenefits, setOriginalBenefits] = useState([]);
  const [addedFeatures, setAddedFeatures] = useState([]);
  const [addedBenefits, setAddedBenefits] = useState([]);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    try {
      // Fetch all business model data in parallel
      const [
        pricingRes,
        recurringRes,
        targetRes,
        productsRes,
        upgradePathsRes,
        uspRes,
        offerRes,
        competitiveRes,
        roadmapRes,
        featuresBenefitsRes
      ] = await Promise.all([
        fetch('/api/business/field/pricing_data', { credentials: 'include' }),
        fetch('/api/business/field/recurring_revenue_model', { credentials: 'include' }),
        fetch('/api/business/field/target_audience', { credentials: 'include' }),
        fetch('/api/products/get-catalog', { credentials: 'include' }),
        fetch('/api/upgrade-paths/get', { credentials: 'include' }),
        fetch('/api/usp/get', { credentials: 'include' }),
        fetch('/api/irresistible-offer/get', { credentials: 'include' }),
        fetch('/api/competitive-analysis/get-saved', { credentials: 'include' }),
        fetch('/api/competitive-analysis/get-roadmap', { credentials: 'include' }),
        fetch('/api/competitive-analysis/get-features-benefits', { credentials: 'include' })
      ]);

      // Parse responses
      if (pricingRes.ok) {
        const data = await pricingRes.json();
        if (data.field_value) {
          try {
            setPricingData(JSON.parse(data.field_value));
          } catch (e) {}
        }
      }

      if (recurringRes.ok) {
        const data = await recurringRes.json();
        if (data.field_value) {
          try {
            setRecurringModel(JSON.parse(data.field_value));
          } catch (e) {}
        }
      }

      if (targetRes.ok) {
        const data = await targetRes.json();
        setTargetAudience(data.field_value || '');
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        const productList = data.catalog || [];
        setProducts(productList);

        // Get core product from Product table (position='core' or infer from upgrade paths)
        let coreFromTable = productList.find(p => p.position?.toLowerCase() === 'core');
        if (!coreFromTable && productList.length > 0) {
          // Fallback: non-tripwire product
          coreFromTable = productList.find(p => !p.is_primary_tripwire && p.position?.toLowerCase() !== 'tripwire');
        }
        if (coreFromTable) {
          setCoreProduct(coreFromTable.name);
        }
      }

      if (upgradePathsRes.ok) {
        const data = await upgradePathsRes.json();
        setUpgradePaths(data.upgrade_paths || []);
      }

      if (uspRes.ok) {
        const data = await uspRes.json();
        setUSP(data.usp || '');
      }

      if (offerRes.ok) {
        const data = await offerRes.json();
        setOffer(data.offer || null);
      }

      if (competitiveRes.ok) {
        const data = await competitiveRes.json();
        setCompetitorCount(data.analysis?.length || 0);
      }

      if (roadmapRes.ok) {
        const data = await roadmapRes.json();
        setRoadmapItems(data.roadmap || []);
      }

      if (featuresBenefitsRes.ok) {
        const data = await featuresBenefitsRes.json();
        setOriginalFeatures(data.original_features || data.features || []);
        setOriginalBenefits(data.original_benefits || data.benefits || []);
        setAddedFeatures(data.added_features || []);
        setAddedBenefits(data.added_benefits || []);
      }

    } catch (err) {
      setError('Error loading business model data: ' + err.message);
    }

    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your business model...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/dashboard/business-model')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <>
      <div className="no-print">
        <Container className="py-4">
          <BackToDashboard module="business-model" />
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Your Business Model</h1>
                <div>
                  <Button variant="primary" onClick={handlePrint} className="me-2">
                    Print Business Model
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/dashboard/business-model')}>
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="foundation-content py-4">

        {/* Core Product & Pricing */}
        <Card className="mb-4 print-section">
          <Card.Body>
            <h2 className="section-title">Core Product & Pricing</h2>

            {coreProduct && (
              <div className="mb-3">
                <strong>Core Product:</strong>
                <p className="mb-0">{coreProduct}</p>
              </div>
            )}

            {pricingData && (
              <>
                <div className="mb-3">
                  <strong>Pricing Model:</strong>
                  <p className="mb-0 text-capitalize">{pricingData.pricing_model?.replace('_', ' ')}</p>
                </div>

                {pricingData.one_time_price && (
                  <div className="mb-3">
                    <strong>Price:</strong>
                    <p className="mb-0">${pricingData.one_time_price}</p>
                  </div>
                )}

                {pricingData.monthly_price && (
                  <div className="mb-3">
                    <strong>Monthly Price:</strong>
                    <p className="mb-0">${pricingData.monthly_price}/month</p>
                  </div>
                )}

                {pricingData.annual_price && (
                  <div className="mb-3">
                    <strong>Annual Price:</strong>
                    <p className="mb-0">${pricingData.annual_price}/year</p>
                  </div>
                )}

                {pricingData.cost_to_deliver && (
                  <div className="mb-3">
                    <strong>Cost to Deliver:</strong>
                    <p className="mb-0">${pricingData.cost_to_deliver}</p>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Recurring Revenue Model */}
        {recurringModel && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Recurring Revenue Model</h2>

              {recurringModel.chosen_model && (
                <div className="mb-3">
                  <strong>Model Type:</strong>
                  <p className="mb-0 text-capitalize">{recurringModel.chosen_model.replace('_', ' ')}</p>
                </div>
              )}

              {recurringModel.pricing_structure && (
                <div className="mb-3">
                  <strong>Pricing Structure:</strong>
                  <p className="mb-0">{recurringModel.pricing_structure}</p>
                </div>
              )}

              {recurringModel.retention_strategy && (
                <div className="mb-3">
                  <strong>Retention Strategy:</strong>
                  <p className="mb-0">{recurringModel.retention_strategy}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Target Market */}
        {targetAudience && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Target Market</h2>
              <div className="mb-3">
                <strong>Primary Target Audience:</strong>
                <p className="mb-0">{targetAudience}</p>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Competitive Position */}
        {competitorCount > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Competitive Intelligence</h2>
              <div className="mb-3">
                <strong>Competitors Analyzed:</strong>
                <p className="mb-0">{competitorCount} competitors researched and analyzed</p>
              </div>
              <p className="text-muted small mb-0">
                Visit the Competitive Analysis page to see detailed competitor research and identified gaps.
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Product Roadmap */}
        {roadmapItems.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Product Roadmap</h2>
              <p className="text-muted mb-3">
                Features and improvements identified from competitive analysis, prioritized by quarter.
              </p>

              {(() => {
                // Group roadmap items by target quarter
                const byQuarter = {};
                roadmapItems.forEach(item => {
                  const quarter = item.target_quarter || 'Unscheduled';
                  if (!byQuarter[quarter]) byQuarter[quarter] = [];
                  byQuarter[quarter].push(item);
                });
                const quarters = Object.keys(byQuarter).sort();

                return quarters.map(quarter => (
                  <div key={quarter} className="mb-4">
                    <h5>
                      <Badge bg="primary" className="me-2">{quarter}</Badge>
                      <small className="text-muted">({byQuarter[quarter].length} items)</small>
                    </h5>
                    <Table bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          <th style={{ width: '100px' }}>Priority</th>
                          <th style={{ width: '120px' }}>Category</th>
                          <th style={{ width: '100px' }}>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byQuarter[quarter].map((item, idx) => (
                          <tr key={idx}>
                            <td><strong>{item.feature_name}</strong></td>
                            <td>
                              <Badge bg={
                                item.priority === 'high' ? 'danger' :
                                item.priority === 'medium' ? 'warning' : 'secondary'
                              }>
                                {item.priority}
                              </Badge>
                            </td>
                            <td><small>{item.category}</small></td>
                            <td className="text-capitalize"><small>{item.status?.replace('_', ' ')}</small></td>
                            <td><small className="text-muted">{item.notes}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ));
              })()}
            </Card.Body>
          </Card>
        )}

        {/* Features & Benefits */}
        {(originalFeatures.length > 0 || originalBenefits.length > 0 || addedFeatures.length > 0 || addedBenefits.length > 0) && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Features & Benefits</h2>

              {/* Original features & benefits from competitive analysis */}
              {(originalFeatures.length > 0 || originalBenefits.length > 0) && (
                <>
                  {(addedFeatures.length > 0 || addedBenefits.length > 0) && (
                    <h5 className="mt-3 mb-2">From Competitive Analysis</h5>
                  )}
                  <Row>
                    {originalFeatures.length > 0 && (
                      <Col md={6}>
                        <strong>Features</strong>
                        <ul className="mb-3">
                          {originalFeatures.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </Col>
                    )}
                    {originalBenefits.length > 0 && (
                      <Col md={6}>
                        <strong>Benefits</strong>
                        <ul className="mb-3">
                          {originalBenefits.map((benefit, idx) => (
                            <li key={idx}>{benefit}</li>
                          ))}
                        </ul>
                      </Col>
                    )}
                  </Row>
                </>
              )}

              {/* User-added features & benefits */}
              {(addedFeatures.length > 0 || addedBenefits.length > 0) && (
                <>
                  <h5 className="mt-3 mb-2">Added by You</h5>
                  <Row>
                    {addedFeatures.length > 0 && (
                      <Col md={6}>
                        <strong>Features</strong>
                        <ul className="mb-3">
                          {addedFeatures.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </Col>
                    )}
                    {addedBenefits.length > 0 && (
                      <Col md={6}>
                        <strong>Benefits</strong>
                        <ul className="mb-3">
                          {addedBenefits.map((benefit, idx) => (
                            <li key={idx}>{benefit}</li>
                          ))}
                        </ul>
                      </Col>
                    )}
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Product Catalog & Value Ladder */}
        {products.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Product Catalog & Value Ladder</h2>

              <Table bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Type</th>
                    <th>Purchase Type</th>
                    <th>Price</th>
                    <th>Position</th>
                    <th>Sequence</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr key={index}>
                      <td><strong>{product.name}</strong></td>
                      <td className="text-capitalize">{product.type}</td>
                      <td className="text-capitalize">{product.purchase_type?.replace(/-/g, ' ')}</td>
                      <td>${product.sale_price}</td>
                      <td>
                        <Badge bg={
                          product.position === 'entry' ? 'success' :
                          product.position === 'core' ? 'primary' :
                          product.position === 'premium' ? 'warning' : 'secondary'
                        }>
                          {product.position}
                        </Badge>
                      </td>
                      <td>{product.promotion_sequence}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {products.some(p => p.reasoning) && (
                <div className="mt-3">
                  <strong>AI Recommendations:</strong>
                  {products.map((product, index) => (
                    product.reasoning && (
                      <div key={index} className="mt-2">
                        <em>{product.name}:</em> {product.reasoning}
                      </div>
                    )
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Customer Journey Mapping */}
        {upgradePaths.length > 0 && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Customer Journey Mapping</h2>
              <p className="text-muted mb-3">
                How customers move through your product ladder with estimated conversion rates and timeframes.
              </p>

              <Table bordered hover className="mb-0">
                <thead>
                  <tr>
                    <th>From Product</th>
                    <th>Upgrades To</th>
                    <th>Conversion Rate</th>
                    <th>Timeframe</th>
                  </tr>
                </thead>
                <tbody>
                  {upgradePaths.map((path, index) => (
                    <tr key={index}>
                      <td>{path.from_product_name}</td>
                      <td><strong>{path.to_product_name}</strong></td>
                      <td>
                        <Badge bg="primary">{path.conversion_rate}%</Badge>
                      </td>
                      <td>
                        {path.time_value} {path.time_unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="mt-3 text-muted small">
                <strong>Note:</strong> These conversion rates and timeframes are estimates based on your business model
                and will be used for revenue projections in your exit strategy.
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Unique Selling Proposition */}
        {usp && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Unique Selling Proposition</h2>
              <div className="bg-light p-3 rounded">
                <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '500' }}>
                  "{usp}"
                </p>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Irresistible Offer */}
        {offer && (
          <Card className="mb-4 print-section">
            <Card.Body>
              <h2 className="section-title">Irresistible Offer</h2>

              {/* Complete Offer */}
              {offer.complete_offer && (
                <div className="mb-4">
                  <strong>Complete Offer:</strong>
                  <div className="bg-light p-3 rounded mt-2">
                    <p className="mb-0">{offer.complete_offer}</p>
                  </div>
                </div>
              )}

              {/* Bonus Stack */}
              {offer.bonus_stack && offer.bonus_stack.length > 0 && (
                <div className="mb-4">
                  <strong>Bonus Stack:</strong>
                  {offer.bonus_stack.map((bonus, index) => (
                    <div key={index} className="mt-2 ps-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <span><strong>Bonus {index + 1}:</strong> {bonus.name}</span>
                        <Badge bg="success">{bonus.perceived_value}</Badge>
                      </div>
                      <p className="mb-0 text-muted small">{bonus.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Guarantee */}
              {offer.guarantee && (
                <div className="mb-4">
                  <strong>Guarantee:</strong>
                  <div className="mt-2 ps-3">
                    <div><Badge bg="info">{offer.guarantee.type}</Badge></div>
                    <div className="mt-1"><strong>{offer.guarantee.headline}</strong></div>
                    <p className="mb-0 text-muted">{offer.guarantee.description}</p>
                  </div>
                </div>
              )}

              {/* Urgency */}
              {offer.urgency && (
                <div className="mb-4">
                  <strong>Urgency Mechanism:</strong>
                  <div className="mt-2 ps-3">
                    <div><Badge bg="warning" text="dark">{offer.urgency.mechanism}</Badge></div>
                    <div className="mt-1"><strong>{offer.urgency.headline}</strong></div>
                    <p className="mb-0 text-muted">{offer.urgency.description}</p>
                  </div>
                </div>
              )}

              {/* Risk Reversal */}
              {offer.risk_reversal && (
                <div className="mb-4">
                  <strong>Risk Reversal:</strong>
                  <div className="mt-2 ps-3">
                    <div><strong>{offer.risk_reversal.strategy}</strong></div>
                    <p className="mb-0 text-muted">{offer.risk_reversal.copy}</p>
                  </div>
                </div>
              )}

              {/* Price Anchoring */}
              {offer.price_anchoring && (
                <div className="mb-0">
                  <strong>Price Anchoring:</strong>
                  <div className="mt-2 ps-3">
                    <div><Badge bg="success">Total Value: {offer.price_anchoring.total_value}</Badge></div>
                    <p className="mb-0 text-muted mt-2">{offer.price_anchoring.copy}</p>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Summary Footer */}
        <Card className="mb-4 print-section border-primary">
          <Card.Body className="bg-light">
            <h3 className="mb-3">🎯 Your Business Model Complete</h3>
            <p className="mb-2">
              You've successfully defined your complete business model with pricing, positioning, and
              conversion-optimized offers. This foundation will guide all your marketing and sales efforts.
            </p>
            <p className="mb-0 text-muted">
              <small>Generated by AI Profit Engineer • Module 2: Business Model</small>
            </p>
          </Card.Body>
        </Card>

      </Container>
    </>
  );
}

export default ViewBusinessModel;
